import EventEmitter from 'events';
import axios from 'axios';

export class WebRTCMedia extends EventEmitter {
    static Events = {
        DATA: 'DATA',
        ERROR: 'ERROR',
    } as const;

    private src: string;
    private pc: RTCPeerConnection | null = null;
    private sessionUrl: string | null = null;
    // private videoEl: HTMLVideoElement | null = null;

    constructor(options: { src: string }) {
        super();
        this.src = options.src;
    }

    init(videoElement: HTMLVideoElement) {
        // this.videoEl = videoElement;

        const pc = new RTCPeerConnection();
        this.pc = pc;

        // Receive-only
        pc.addTransceiver('video', { direction: 'recvonly' });
        pc.addTransceiver('audio', { direction: 'recvonly' });

        // When a track arrives
        pc.ontrack = (ev) => {
            if (ev.track.kind === 'video' && videoElement) {
                videoElement.srcObject = ev.streams[0];
                this.emit(WebRTCMedia.Events.DATA);
            }
        };

        // Trickle ICE
        pc.onicecandidate = (ev) => {
            if (!ev.candidate || !this.sessionUrl) return;

            const mid = ev.candidate.sdpMid ?? '0';
            const kind = mid === 'audio' ? 'audio' : 'video';
            const cand = ev.candidate.candidate.startsWith('candidate:')
                ? ev.candidate.candidate
                : `candidate:${ev.candidate.candidate}`;
            const frag = `m=${kind} 9 UDP/TLS/RTP/SAVPF 96\r\n` + `a=mid:${mid}\r\n` + `a=${cand}\r\n\r\n`;

            axios
                .patch(this.sessionUrl, frag, {
                    headers: { 'Content-Type': 'application/trickle-ice-sdpfrag' },
                })
                .catch(() => {
                    // ignore non-fatal ICE errors
                });
        };

        // Create offer and send it
        pc.createOffer()
            .then((offer) => {
                return pc.setLocalDescription(offer).then(() => offer);
            })
            .then((offer) => {
                return axios.post(this.src, offer.sdp!, {
                    headers: { 'Content-Type': 'application/sdp', Accept: 'application/sdp' },
                });
            })
            .then((response) => {
                const loc = response.headers['location'] || response.headers['Location'];
                this.sessionUrl = new URL((loc || '').replace('/live/', '/webrtc-streams/'), this.src).toString();

                return pc.setRemoteDescription({ type: 'answer', sdp: response.data });
            })
            .catch((err) => {
                this.emit(WebRTCMedia.Events.ERROR, err);
            });
    }

    destroy() {
        if (this.pc) {
            try {
                this.pc.close();
            } catch {
                /* ignore */
            }
            this.pc = null;
        }

        if (this.sessionUrl) {
            axios.delete(this.sessionUrl).catch(() => {
                /* ignore */
            });
            this.sessionUrl = null;
        }
    }
}
